import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatEUR } from "../lib/api";
import { CreditCard, ShieldCheck, Loader2, Banknote, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import PayPalCheckout from "../components/PayPalCheckout";

const Checkout = () => {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [address, setAddress] = useState(user?.default_address || "");
  const [notes, setNotes] = useState("");
  const [deliverySlot, setDeliverySlot] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [processing, setProcessing] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  if (!user) { nav("/"); return null; }
  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-empty">
        <p className="text-slate-500">Il carrello è vuoto.</p>
      </div>
    );
  }

  const placeCash = async () => {
    if (!deliveryDate) { toast.error("Scegli una data"); return; }
    if (!deliverySlot) { toast.error("Scegli una fascia"); return; }
    try {
      setProcessing(true);
      const r = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_address: address,
        delivery_slot: deliverySlot,
        delivery_date: format(deliveryDate, "yyyy-MM-dd"),
        payment_method: "cash",
        notes,
      });
      clear();
      toast.success("Ordine confermato!", {
        description: `Pagherai €${r.data.total.toFixed(2)} alla consegna.`,
      });
      nav("/orders");
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally {
      setProcessing(false);
    }
  };

  const onPayPalSuccess = (order) => {
    clear();
    toast.success("Pagamento completato!", { description: `Ordine ${order.order_id.slice(-8)} confermato.` });
    nav("/orders");
  };

  const onPayPalError = (msg) => {
    toast.error("Errore pagamento", { description: msg });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-5 gap-10" data-testid="checkout-page">
      <div className="lg:col-span-3 space-y-8">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-light text-[#032B44]">Checkout</h1>
          <p className="text-slate-600 mt-2">Completa il tuo ordine in modo sicuro.</p>
        </div>

        <section className="bg-white border border-sky-100 rounded-2xl p-7">
          <h2 className="font-heading text-lg font-medium text-[#032B44] mb-5">Indirizzo di consegna</h2>
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Via, numero civico, città, provincia, CAP"
            className="w-full bg-white border border-sky-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent transition-all min-h-[90px]"
            data-testid="checkout-address"
          />
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Note per la consegna (opzionale)"
            className="w-full mt-3 bg-white border border-sky-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent transition-all"
            data-testid="checkout-notes"
          />
        </section>

        <section className="bg-white border border-sky-100 rounded-2xl p-7">
          <h2 className="font-heading text-lg font-medium text-[#032B44] mb-5">Quando vuoi ricevere l'ordine?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-wider uppercase font-bold text-sky-600 block mb-1.5">Data consegna</label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`w-full h-11 px-4 rounded-lg border text-left text-sm flex items-center gap-2 transition-all ${deliveryDate ? "border-[#00A3FF] bg-sky-50 text-[#032B44] font-medium" : "border-sky-200 bg-white text-slate-500"}`}
                    data-testid="delivery-date-trigger"
                  >
                    <CalendarIcon className="w-4 h-4 text-sky-600" />
                    {deliveryDate ? format(deliveryDate, "EEEE d MMMM yyyy", { locale: it }) : "Scegli una data"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    locale={it}
                    selected={deliveryDate}
                    onSelect={(d) => { setDeliveryDate(d); setDatePickerOpen(false); }}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    data-testid="delivery-calendar"
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-xs tracking-wider uppercase font-bold text-sky-600 block mb-1.5">Fascia</label>
              <div className="grid grid-cols-2 gap-2">
                <SlotChoice
                  active={deliverySlot === "mattina"}
                  onClick={() => setDeliverySlot("mattina")}
                  title="Mattina"
                  desc="08:00–12:30"
                  testId="slot-mattina"
                />
                <SlotChoice
                  active={deliverySlot === "pomeriggio"}
                  onClick={() => setDeliverySlot("pomeriggio")}
                  title="Pomeriggio"
                  desc="15:00–19:00"
                  testId="slot-pomeriggio"
                />
              </div>
            </div>
          </div>
          {user?.phone && deliveryDate && deliverySlot && (
            <p className="text-xs text-slate-500 mt-4">
              Ti contatteremo al <span className="font-mono text-[#032B44]">{user.phone}</span> il <span className="font-medium text-[#032B44]">{format(deliveryDate, "d MMM", { locale: it })}</span> per confermare l'orario esatto.
            </p>
          )}
        </section>

        <section className="bg-white border border-sky-100 rounded-2xl p-7">
          <h2 className="font-heading text-lg font-medium text-[#032B44] mb-5">Metodo di pagamento</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PayMethod
              active={paymentMethod === "paypal"}
              onClick={() => setPaymentMethod("paypal")}
              title="PayPal · Carta"
              desc="Account PayPal o Visa/Mastercard"
              testId="pay-paypal"
              logo={
                <span className="font-heading font-bold text-lg">
                  <span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span>
                </span>
              }
            />
            <PayMethod
              active={paymentMethod === "cash"}
              onClick={() => setPaymentMethod("cash")}
              title="Alla consegna"
              desc="Contanti al corriere"
              testId="pay-cash"
              logo={<Banknote className="w-6 h-6 text-emerald-600" />}
            />
          </div>
          {paymentMethod === "cash" ? (
            <div className="mt-4 flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 p-3 rounded-lg border border-emerald-200">
              <Banknote className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <p>Pagherai in contanti al corriere al momento della consegna. Assicurati di avere l'importo esatto.</p>
            </div>
          ) : (
            <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-sky-50/50 p-3 rounded-lg border border-sky-100">
              <ShieldCheck className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
              <p>Pagamento elaborato da PayPal. Puoi pagare con il tuo conto PayPal oppure con Carta di credito/debito (Visa, Mastercard, Amex) senza creare un account.</p>
            </div>
          )}
        </section>
      </div>

      <aside className="lg:col-span-2">
        <div className="lg:sticky lg:top-24 bg-sky-50/40 border border-sky-100 rounded-2xl p-7">
          <h2 className="font-heading text-lg font-medium text-[#032B44] mb-5">Riepilogo</h2>
          <ul className="space-y-3 mb-5">
            {items.map((i) => (
              <li key={i.product_id} className="flex justify-between text-sm">
                <span className="text-slate-700">{i.name} × {i.quantity}</span>
                <span className="font-medium text-[#032B44]">{formatEUR(i.unit_price * i.quantity)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-sky-200 pt-4 flex items-center justify-between">
            <span className="text-sm text-slate-600">Totale</span>
            <span className="font-heading text-3xl font-semibold text-[#032B44]" data-testid="checkout-total">{formatEUR(total)}</span>
          </div>
          {paymentMethod === "cash" ? (
            <button
              onClick={placeCash}
              disabled={processing || !address.trim() || !deliverySlot || !deliveryDate}
              className="w-full mt-6 h-12 rounded-lg bg-[#032B44] hover:bg-[#00A3FF] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              data-testid="place-order-button"
            >
              {processing ? <><Loader2 className="w-4 h-4 animate-spin" /> Elaborazione…</> : <>Conferma ordine · {formatEUR(total)}</>}
            </button>
          ) : (
            <div className="mt-6" data-testid="paypal-pay-area">
              <PayPalCheckout
                cartItems={items}
                addressValid={address.trim() || null}
                deliveryDate={deliveryDate ? format(deliveryDate, "yyyy-MM-dd") : null}
                deliverySlot={deliverySlot}
                notes={notes}
                onSuccess={onPayPalSuccess}
                onError={onPayPalError}
              />
              {(!address.trim() || !deliverySlot || !deliveryDate) && (
                <p className="text-xs text-amber-700 mt-2 text-center">Completa indirizzo, data e fascia per abilitare il pagamento.</p>
              )}
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-3 text-center">
            Cliccando "Paga" accetti i termini di servizio.
          </p>
        </div>
      </aside>
    </div>
  );
};

const SlotChoice = ({ active, onClick, title, desc, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-left p-4 rounded-xl border transition-all ${
      active
        ? "border-[#00A3FF] bg-sky-50 ring-2 ring-[#00A3FF]/20"
        : "border-sky-200 bg-white hover:border-sky-300"
    }`}
  >
    <p className="font-heading text-lg font-medium text-[#032B44]">{title}</p>
    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
  </button>
);

const PayMethod = ({ active, onClick, title, desc, logo, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-left p-4 rounded-xl border transition-all ${
      active
        ? "border-[#00A3FF] bg-sky-50 ring-2 ring-[#00A3FF]/20"
        : "border-sky-200 bg-white hover:border-sky-300"
    }`}
  >
    <div className="flex items-center justify-between mb-2">
      {logo}
      <div className={`w-4 h-4 rounded-full border-2 ${active ? "border-[#00A3FF] bg-[#00A3FF]" : "border-slate-300"}`} />
    </div>
    <p className="text-sm font-medium text-[#032B44]">{title}</p>
    <p className="text-xs text-slate-500">{desc}</p>
  </button>
);

export default Checkout;
